import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import reportWebVitals from "./reportWebVitals";

import JournalDashboard from "./pages/JournalDashboard";
import User from "./pages/User";
import Forms from "./pages/Forms";
import Consents from "./pages/Consents";
import ConsentForm from "./pages/ConsentForm";
import JournalPage from "./pages/JournalPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Chat from "./pages/Chat";
import Files from "./pages/Files";
import {ChakraProvider} from "@chakra-ui/react";
import SleepDiaryPage from "./components/SleepDiary/v2/SleepDiaryPage";
import PrescriptionCompliance from "./components/PrescriptionCompliance/PrescriptionCompliance";
import TempForm from "./components/TempForm";
import Account from "./pages/Account";
import AppointmentsPage from "./pages/Appointments";
import Security from "./pages/Security";
import DependentRoute from "./components/Dependent/DependentRoute";
import DependentHome from "./components/Dependent/DependentHome";
import DependentProfile from "./components/Dependent/DependentProfile";
import DependentAppointments from "./components/Dependent/DependentAppointments";
import DependentForms from "./components/Dependent/DependentForms";
import DependentBilling from "./components/Dependent/DependentBilling";
import DependentDocuments from "./components/Dependent/DependentDocuments";
import {ErrorBoundary} from "./components/ErrorBoundary";
import {installGlobalErrorHandlers} from "./lib/reportError";
import {QueryClientProvider} from "@tanstack/react-query";
import {queryClient} from "./lib/queryClient";

// Capture uncaught errors and unhandled promise rejections from the start,
// and set the hostname-derived tenant tag used by reportError.
installGlobalErrorHandlers();

function AppRoutes() {
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}`;

  return (
    <ErrorBoundary resetKey={routeKey}>
      <Routes location={location} key={routeKey}>
        <Route path="/login/:db" element={<Login />}></Route>
        <Route path="/login" element={<Login />}></Route>
        <Route path="/register" element={<Register />}></Route>
        {/*
          The six routes a guardian can reach for a dependent render DIFFERENT
          components in dependent mode — see `DependentRoute`. The screens below
          always show the signed-in account holder's own record and are never
          handed somebody else's, which is what makes a stale or mis-routed
          switch harmless.
        */}
        <Route
          path="/"
          element={<DependentRoute section="overview" self={JournalDashboard} dependent={DependentHome} />}
        ></Route>
        <Route
          path="/appointments"
          element={
            <DependentRoute
              section="appointments"
              self={AppointmentsPage}
              dependent={DependentAppointments}
            />
          }
        ></Route>
        <Route path="/inbox" element={<Navigate to="/chat" state={location.state} replace />}></Route>
        <Route
          path="/user"
          element={<DependentRoute section="profile" self={User} dependent={DependentProfile} />}
        ></Route>
        <Route
          path="/forms"
          element={<DependentRoute section="forms" self={Forms} dependent={DependentForms} />}
        ></Route>
        <Route path="/consents" element={<Consents />}></Route>
        {/* Public, no session required — reached from the emailed capability
        link. Sits beside /temp-form, which is public for the same reason. */}
        <Route path="/consent-form" element={<ConsentForm />}></Route>
        <Route path="/security" element={<Security />}></Route>
        <Route path="/journal" element={<JournalPage />}></Route>
        {/* Retired 14-10-2026: /journal-old answered through /getBotResponse and
            /promptAI with portal-written prompts. /journal is the journal. */}
        <Route path="/journal-old" element={<Navigate to="/journal" replace />} />
        {/* Retired 30-09-2026: the intake chatbot and its disorder/symptom
            predictions were clinical AI talking to patients (S14/S21). */}
        <Route path="/chatbot" element={<Navigate to="/" replace />} />
        <Route path="/intake" element={<Navigate to="/" replace />} />
        {/* One sleep diary. The old tracker wrote every entry under a fixed
            patient id, so none was ever saved (0 in any clinic, 30-09-2026). */}
        <Route path="/sleepTracker" element={<Navigate to="/sleep" replace />} />
        <Route path="/sleep" element={<SleepDiaryPage />}></Route>
        <Route
          path="/prescriptionCompliance"
          element={<PrescriptionCompliance />}
        ></Route>
        {/* The legacy /payments page is gone. It read the legacy `billing`
            collection and charged cards through /executePayment. The patient
            pays from /account (canonical: /patient-app/billing/collect). */}
        <Route path="/payments" element={<Navigate to="/account" replace />} />
        <Route path="/chat" element={<Chat />}></Route>
        <Route
          path="/account"
          element={<DependentRoute section="billing" self={Account} dependent={DependentBilling} />}
        ></Route>
        <Route
          path="/account/:tab"
          element={<DependentRoute section="billing" self={Account} dependent={DependentBilling} />}
        ></Route>
        <Route
          path="/billing"
          element={<DependentRoute section="billing" self={Account} dependent={DependentBilling} />}
        ></Route>
        <Route
          path="/files"
          element={
            <DependentRoute section="documents" self={Files} dependent={DependentDocuments} />
          }
        ></Route>
        <Route path="/temp-form" element={<TempForm />}></Route>
      </Routes>
    </ErrorBoundary>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter>
    <ChakraProvider>
      <QueryClientProvider client={queryClient}>
        <AppRoutes />
      </QueryClientProvider>
    </ChakraProvider>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
//reportWebVitals(console.log);
